// One person's half-day in the grid, in one of the shared states (see
// lib/blockState.ts): planned, now, done (≥ 90%), short (70–89% or an issue),
// under (< 70%), unlogged, float or off. Shows made/target, the learner's
// reduced target, and a lock once the half-day has started.
import type { Cell, Day, Half, SessionRow } from "../lib/types";
import { blockInfo } from "../lib/blockState";
import { chainOf } from "../lib/chain";
import { Glyph } from "./Glyph";

export interface BlockCellProps {
  cell: Cell;
  taskName?: string | null;
  group?: string | null;
  sessionQty?: number | null;
  halfOnly?: Half | null;
  session?: SessionRow | null;
  now?: { day: Day; half: Half } | null;
  lockedBefore?: { day: Day; half: Half } | null;
  /** Edit mode: unlocked cells are buttons. */
  editable?: boolean;
  selected?: boolean;
  changed?: boolean;
  violation?: "block" | "warn" | null;
  night?: boolean;
  /** Show the person's name (the Tasks page lists cells by task). */
  showPerson?: boolean;
  celebrating?: boolean;
  onSelect?: (cell: Cell) => void;
}

const STATE_WORD: Record<string, string> = {
  done: "done", short: "short", under: "under target", unlogged: "not logged", now: "now", planned: "planned", float: "float", off: "not in"
};

export function BlockCell(props: BlockCellProps) {
  const { cell, taskName, group, session, editable, selected, changed, violation, night, showPerson, celebrating, onSelect } = props;
  const info = blockInfo(cell, { session, sessionQty: props.sessionQty, now: props.now, lockedBefore: props.lockedBefore });
  const chain = cell.kind === "task" ? chainOf(cell.task, group) : cell.kind;
  const result = ["done", "short", "under"].includes(info.state);
  const cls = [
    "lb", `lb--${info.state}`, `lb--${chain}`,
    info.state === "now" || info.state === "unlogged" ? "lb--planned" : "",
    night ? "lb--night" : "",
    selected ? "is-selected" : "", changed ? "is-changed" : "", info.locked ? "is-locked" : "",
    violation === "block" ? "has-block" : violation === "warn" ? "has-warn" : "",
    celebrating ? "is-celebrating" : ""
  ].filter(Boolean).join(" ");

  const numbers = cell.kind === "task"
    ? result || info.made != null ? `${info.made ?? 0} / ${info.target ?? "?"}` : info.target != null ? `${info.target}${cell.learner && !night ? " (learner)" : ""}` : ""
    : cell.kind === "float" ? (info.made != null ? `${info.made} made` : "Float") : cell.kind === "other" ? "Other work" : "Not in";
  const label = `${cell.person}, ${cell.day} ${cell.half}: ${cell.kind === "task" ? `${cell.task} ${taskName ?? ""}` : cell.kind}, ${numbers}, ${STATE_WORD[info.state]}${info.locked ? ", locked" : ""}`;

  const body = (
    <>
      <span className="lb__top">
        {cell.kind === "task" ? <span className="lb__id">{cell.task}</span> : null}
        {cell.kind === "task" && taskName ? <span className="lb__t">{taskName}</span> : null}
        {showPerson ? <span className="lb__who">{cell.person}</span> : null}
        <span className="lb__icons">
          {props.halfOnly ? <Glyph name="clock" size={night ? 20 : 11} title={props.halfOnly === "AM" ? "Morning only" : "Afternoon only"} /> : null}
          {cell.learner ? <span className="lb__learner" title={cell.expert ? `Learning, with ${cell.expert}` : "Learning"} /> : null}
          {editable && info.locked ? <Glyph name="lock" size={11} title="Locked" /> : null}
          {session?.issue ? <Glyph name="warn" size={night ? 20 : 11} title={session.issue} /> : null}
        </span>
      </span>
      <span className="lb__n">{numbers}</span>
    </>
  );

  if (editable && !info.locked && onSelect) {
    return (
      <button type="button" className={cls} aria-label={label} aria-pressed={selected} data-state={info.state} onClick={() => onSelect(cell)}>
        {body}
      </button>
    );
  }
  return (
    <div className={cls} aria-label={label} data-state={info.state} title={session?.note ?? undefined}
      onClick={onSelect ? () => onSelect(cell) : undefined} role={onSelect ? "button" : undefined}>
      {body}
    </div>
  );
}
